"use client";

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import Snackbar from '@/components/common/Snackbar';

type SnackbarType = 'success' | 'error' | 'info';

interface SnackbarContextType {
    showSnackbar: (message: string, type?: SnackbarType, duration?: number) => void;
    hideSnackbar: () => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

export const useSnackbar = () => {
    const context = useContext(SnackbarContext);
    if (!context) {
        throw new Error('useSnackbar must be used within a SnackbarProvider');
    }
    return context;
};

interface SnackbarProviderProps {
    children: ReactNode;
}

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({ children }) => {
    const [message, setMessage] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [type, setType] = useState<SnackbarType>('info');
    const [duration, setDuration] = useState(3000);

    const showSnackbar = useCallback((msg: string, t: SnackbarType = 'info', d: number = 3000) => {
        setMessage(msg);
        setType(t);
        setDuration(d);
        setIsOpen(true);
    }, []);

    const hideSnackbar = useCallback(() => {
        setIsOpen(false);
    }, []);

    return (
        <SnackbarContext.Provider value={{ showSnackbar, hideSnackbar }}>
            {children}
            <Snackbar
                message={message}
                isOpen={isOpen}
                type={type}
                duration={duration}
                onClose={hideSnackbar}
            />
        </SnackbarContext.Provider>
    );
};
